/**
 * ExclusionReasonSection — surfaces WHY this item is excluded or passed
 * through. Replaces the prior inline ReasonSection so the hover card always
 * answers "what is happening with this item".
 *
 * The rules evaluator emits human-readable reasons of the form
 * `metric op value` (e.g. `profit < 0`), which we render inside a <code>
 * tag for clarity. Pass-through cases (inactive item, no formula targets
 * this layer) get a softer info banner so users know the item is alive but
 * frozen at its current weight.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { HoverSection } from '../hover-primitives';
import type { PreviewRow } from '../../../preview-row.types';

export interface ExclusionReasonSectionProps {
  row: PreviewRow;
}

const ExclusionReasonSection: FC<ExclusionReasonSectionProps> = ({ row }) => {
  if (row.ruleExcluded) return <RuleExcludedBanner reason={row.reason} />;
  if (row.excluded) return <ExcludedBanner reason={row.reason} />;
  if (row.passThrough) return <PassThroughBanner reason={row.reason} />;
  return null;
};

const RuleMatch: FC<{ reason?: string }> = ({ reason }) =>
  reason ? (
    <>
      Matched condition:{' '}
      <code className="rounded bg-destructive/10 px-1 py-0.5 font-mono text-[10px]">
        {reason}
      </code>
    </>
  ) : (
    <>No specific rule reason was recorded.</>
  );

const RuleExcludedBanner: FC<{ reason?: string }> = ({ reason }) => (
  <HoverSection label="Excluded by rule">
    <p className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px] leading-snug text-destructive">
      Kept at its current weight and IGNORED in the optimization, so the
      non-excluded items share the full 100%. Never zeroed, never disabled.{' '}
      <RuleMatch reason={reason} />
    </p>
  </HoverSection>
);

const ExcludedBanner: FC<{ reason?: string }> = ({ reason }) => (
  <HoverSection label="Excluded by rule">
    <p className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px] leading-snug text-destructive">
      This item keeps its current weight and is left out of the optimization
      (only the non-excluded items are re-optimized). <RuleMatch reason={reason} />
    </p>
  </HoverSection>
);

const PassThroughBanner: FC<{ reason?: string }> = ({ reason }) => (
  <HoverSection label="Pass-through">
    <p className="rounded border border-sky-400/30 bg-sky-500/5 px-2 py-1.5 text-[11px] leading-snug text-sky-700 dark:text-sky-300">
      Kept at current weight — the optimizer is not redistributing this item.
      {reason ? <span className="ml-1 italic">({reason})</span> : null}
    </p>
  </HoverSection>
);

export default ExclusionReasonSection;
